import React, { createContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { isAfter, parse } from 'date-fns';

import URLS from 'constants/urls';
import { transformV2ClientResponse } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import { makePath } from 'helpers/link-generation';
import { selectActiveVenueId } from 'selectors/general';

import { EDITABLE_CAMPAIGN_LEVELS, EDITABLE_CAMPAIGN_TYPES } from '../Campaigns.helpers';

/**
 * @typedef {Object} Value
 * @property {Object[]} allCampaigns
 * @property {Object[]} activeCampaigns
 * @property {Object[]} inactiveCampaigns
 * @property {Object[]} campaignOptions
 * @property {Object} totalRevenueFromCampaigns
 * @property {number} totalRevenueFromCampaigns.amount
 * @property {string} totalRevenueFromCampaigns.currency
 */

const DEFAULT_VALUE = {
  allCampaigns: [],
  activeCampaigns: [],
  inactiveCampaigns: [],
  campaignOptions: [],
  totalRevenueFromCampaigns: { amount: 0, currency: null },
};

const CACHE = new Map();
const CACHE_EVENT_TARGET = new EventTarget();
const CACHE_REFRESH_EVENT = 'cacheRefresh';

export function refreshCampaignCacheForVenueId(key) {
  CACHE.delete(key);

  CACHE_EVENT_TARGET.dispatchEvent(new CustomEvent(CACHE_REFRESH_EVENT, { detail: { key } }));
}

async function fetchCampaigns(venueId) {
  try {
    const url = URLS.campaigns.read(venueId);
    const response = await httpClient.get(url);
    const { campaigns } = transformV2ClientResponse(response);

    return campaigns;
  } catch (error) {
    reportError(error);

    return [];
  }
}

async function fetchCampaignOptions(venueId) {
  try {
    const url = URLS.campaigns.options(venueId);
    const response = await httpClient.get(url);
    const { campaignTypeOptions } = transformV2ClientResponse(response);

    return campaignTypeOptions;
  } catch (error) {
    reportError(error);

    return [];
  }
}

function isCampaignActive({ hasEnded }) {
  return !hasEnded;
}

function prepareCampaigns(venueId, campaigns) {
  const today = new Date();

  return campaigns.reduce(
    (acc, campaign) => {
      const {
        startDate,
        endDate,
        discountPercentage,
        partnerCampaignId,
        partnerCampaignType: { name, level },
        revenue: { amount, currency },
      } = campaign;

      const startDateTime = parse(startDate, 'yyyy-MM-dd', new Date());
      const endDateTime = !endDate ? null : parse(endDate, 'yyyy-MM-dd', new Date());

      const hasStarted = isAfter(today, startDateTime);
      const hasEnded = !endDate ? false : isAfter(today, endDateTime);

      const discountDecimal = discountPercentage / 100;

      const isEditable =
        partnerCampaignId &&
        EDITABLE_CAMPAIGN_TYPES.includes(name) &&
        EDITABLE_CAMPAIGN_LEVELS.includes(level) &&
        !hasEnded;

      acc.compatCampaigns.push({
        discountDecimal,
        hasStarted,
        hasEnded,
        startDateTime,
        endDateTime,
        amount,
        currency,
        isEditable,
        editUrl: !isEditable ? null : makePath.campaignsEdit({ venueId, partnerCampaignId }),
        ...campaign,
      });

      acc.totalRevenue.amount += amount;
      acc.totalRevenue.currency = currency;

      return acc;
    },
    { compatCampaigns: [], totalRevenue: { ...DEFAULT_VALUE.totalRevenueFromCampaigns } }
  );
}

function splitActiveAndInactiveCampaigns(allCampaigns) {
  return allCampaigns.reduce(
    (acc, campaign) => {
      if (isCampaignActive(campaign)) {
        acc.activeCampaigns.push(campaign);
      } else {
        acc.inactiveCampaigns.push(campaign);
      }

      return acc;
    },
    { activeCampaigns: [], inactiveCampaigns: [] }
  );
}

const CampaignsContext = createContext(DEFAULT_VALUE);

export function CampaignsContextProvider({ children }) {
  const venueId = useSelector(selectActiveVenueId);
  const [value, setValue] = useState(CACHE.get(venueId) || DEFAULT_VALUE);

  async function fetchCampaignsAndOptions() {
    const [allCampaigns, campaignOptions] = await Promise.all([fetchCampaigns(venueId), fetchCampaignOptions(venueId)]);

    const { compatCampaigns, totalRevenue } = prepareCampaigns(venueId, allCampaigns);
    const { activeCampaigns, inactiveCampaigns } = splitActiveAndInactiveCampaigns(compatCampaigns);

    return {
      allCampaigns: compatCampaigns,
      activeCampaigns,
      inactiveCampaigns,
      campaignOptions,
      isAnyCampaignOptionAvailable: campaignOptions.some(campaign => campaign.available),
      totalRevenueFromCampaigns: totalRevenue,
    };
  }

  async function updateFromCacheOrFetch() {
    if (CACHE.has(venueId)) {
      setValue(CACHE.get(venueId));
    } else {
      const newValue = await fetchCampaignsAndOptions();
      setValue(newValue);
      CACHE.set(venueId, newValue);
    }
  }

  useEffect(() => {
    if (!venueId) {
      return;
    }

    updateFromCacheOrFetch();
  }, [venueId]);

  useEffect(() => {
    CACHE_EVENT_TARGET.addEventListener(CACHE_REFRESH_EVENT, updateFromCacheOrFetch);

    return () => {
      CACHE_EVENT_TARGET.removeEventListener(CACHE_REFRESH_EVENT, updateFromCacheOrFetch);
    };
  }, []);

  return <CampaignsContext.Provider value={value}> {children} </CampaignsContext.Provider>;
}

CampaignsContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]),
};

export default CampaignsContext;
